import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../@/components/ui/select";
import { Button } from "../../@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { api_endpoint } from "@/constants/constant";
import { useAuth } from "@clerk/clerk-react";
import { ClipLoader } from "react-spinners";

export default function EditPathwayEnrollmentDialog({
  open,
  onOpenChange,
  enrollment,
  pathways,
  onUpdated,
}) {
  const { getToken } = useAuth();
  const [newPathwayId, setNewPathwayId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setNewPathwayId("");
  }, [open]);

  const oldPathwayId = enrollment?.pathway_id;

  const pathwayOptions = useMemo(() => {
    const list = Array.isArray(pathways) ? pathways : [];
    return list
      .map((p) => ({
        id: p?.id,
        name: p?.name || p?.title,
      }))
      .filter((p) => p.id != null && String(p.name || "").trim().length);
  }, [pathways]);

  const oldPathwayName = useMemo(() => {
    const match = pathwayOptions.find((p) => Number(p.id) === Number(oldPathwayId));
    return match?.name || enrollment?.pathway || "Not available";
  }, [pathwayOptions, oldPathwayId, enrollment]);

  const handleSave = async () => {
    if (!enrollment?.user_id) {
      toast.error("Missing user id for this enrolment.");
      return;
    }
    if (!oldPathwayId && oldPathwayId !== 0) {
      toast.error("Missing old pathway id for this enrolment.");
      return;
    }
    if (!newPathwayId) {
      toast.error("Please select a new pathway.");
      return;
    }
    if (Number(newPathwayId) === Number(oldPathwayId)) {
      toast.error("Please select a different pathway.");
      return;
    }

    setSaving(true);
    try {
      const token = await getToken();
      if (!token) return;

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const payload = {
        user_id: enrollment.user_id,
        old_pathway_id: Number(oldPathwayId),
        new_pathway_id: Number(newPathwayId),
      };

      console.log("Updating pathway enrolment with payload:", payload);

      await axios.put(api_endpoint + "/pathway/enrollment/edit", payload, reqConfig);

      toast.success("Enrolment updated successfully.");
      onOpenChange?.(false);
      onUpdated?.();
    } catch (error) {
      console.error("Failed to update pathway enrolment", error);
      toast.error("Failed to update enrolment. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const isDisabled = saving;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md h-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-(--neutral-900)">
            Edit Enrolment
          </DialogTitle>
          <DialogDescription className="font-body text-(--neutral-700)">
            Move{" "}
            <span className="font-semibold">
              {enrollment?.name || enrollment?.email || "this user"}
            </span>{" "}
            from{" "}
            <span className="font-semibold">{oldPathwayName}</span> to a new pathway.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-2">
            <div className="font-body text-sm text-(--neutral-700)">
              New Pathway
            </div>
            <Select value={newPathwayId} onValueChange={setNewPathwayId}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a pathway" />
              </SelectTrigger>
              <SelectContent>
                {pathwayOptions.map((p) => (
                  <SelectItem key={String(p.id)} value={String(p.id)}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" disabled={isDisabled}>
                Cancel
              </Button>
            </DialogClose>
            <Button variant="" onClick={handleSave} disabled={isDisabled || !newPathwayId}>
              {saving ? <ClipLoader size={18} color="#fff" /> : "Save"}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
